import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export function ContactUsPage() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.replace(/^#/, "");
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash]);

  return (
    <main>
      <Main />
      <Content />
    </main>
  );
}

function Main() {
  const { t } = useTranslation();
  
  return (
    <div
      className="py-16 relative md:h-[485px]"
      style={{
        backgroundImage: "url(/assets/img/main-contact.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="container px-8 md:px-[100px] py-[80px] relative z-10">
        <h1 className="text-left text-[40px] md:text-5xl font-light text-white mb-8">
          {t('ContactUsPage.main.title')}
        </h1>
        <p className="text-left md:leading-[32px] text-base md:text-xl font-light text-white mb-2">
          {t('ContactUsPage.main.question1')}
        </p>
        <p className="text-left text-base md:text-xl font-light text-white mb-8">
          {t('ContactUsPage.main.question2')}
        </p>
      </div>
    </div>
  );
}

function Content() {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    city: "",
    project: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const endpoint =
        (process.env.REACT_APP_EMAIL_ENDPOINT as string) || "/api/send-email"

      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          project: formData.project,
          message: formData.message,
        }),
      });
      const result = await response.json();
      if (result?.success) {
        setSubmitSuccess(true);
        setTimeout(() => {
          setSubmitSuccess(false);
          setFormData({
            name: "",
            company: "",
            email: "",
            phone: "",
            city: "",
            project: "",
            message: "",
          });
        }, 3000);
      } else {
        throw new Error(result?.message || "Error al enviar el formulario");
      }
    } catch (err) {
      console.error("Resend endpoint error", err);
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="form" className="bg-[#F2F6FB] pb-32 w-full relative">
      <div className="mx-auto px-8 md:px-28 pt-16 relative z-10">
        <div className="xl:flex space-y-12 md:space-y-0 justify-between">
          <div className="w-full flex flex-col text-left">
            <h2 className="md:text-6xl text-4xl font-light text-left">
              {t('ContactUsPage.content.officesTitle')}
            </h2>

            <div className="bg-[#F2F6FB] py-16 relative ">
              <div className="space-y-8 relative z-10">
                <div className="flex items-center space-x-3">
                  <img
                    className="w-[32px] h-[32px]"
                    src="/assets/svg/location.svg"
                    alt="Location"
                    loading="lazy"
                    decoding="async"
                  />
                  <p className="text-left">
                    {t('ContactUsPage.content.address')}
                  </p>
                </div>

                <div className="flex items-center space-x-3">
                  <img
                    className="w-[32px] h-[32px]"
                    src="/assets/svg/mail.svg"
                    alt="Email"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="text-left">
                    <a href={`mailto:${t('ContactUsPage.content.email')}`}>{t('ContactUsPage.content.email')}</a>
                  </div>
                </div>

                <div className="w-full space-y-2">
                  <div className="flex items-center gap-4">
                    <img
                      className="w-[32px] h-[32px]"
                      src="/assets/svg/phone.svg"
                      alt="Phone"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="space-y-4 text-left">
                      <p>
                        <a href={`tel:${t('ContactUsPage.content.phone1')}`}>
                          {t('ContactUsPage.content.phone1')}
                        </a>
                      </p>
                      <p>
                        <a href={`tel:${t('ContactUsPage.content.phone2')}`}>
                          {t('ContactUsPage.content.phone2')}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-8 w-full">
                  <div className="flex items-center space-x-3">
                    <img
                      className="w-[32px] h-[32px]"
                      src="/assets/svg/whatsapp.svg"
                      alt="Whatsapp"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="text-left">
                      <a href="https://wa.me/8115135735">{t('ContactUsPage.content.whatsapp')}</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full text-left relative">
            <h2 className="md:text-[40px] text-[32px] text-blue-A font-medium text-left md:leading-[80px] leading-[50px]">
              {t('ContactUsPage.content.formTitle')}
            </h2>
            <p className="text-left md:leading-[32px] text-base md:text-xl font-normal text-blue-A mb-8">
              {t('ContactUsPage.content.formSubtitle')}
            </p>
            <form  className="space-y-4" onSubmit={handleSubmit}>
              <div className="md:flex gap-24 space-y-4 md:space-y-0">
                <div className="w-full space-y-2">
                  <label
                    className="text-[#21272A] font-medium text-left text-base md:text-lg"
                    htmlFor="name"
                  >
                    {t('ContactUsPage.content.form.name')}
                  </label>
                  <input
                    type="text"
                    required
                    id="name"
                    name="name"
                    placeholder={t('ContactUsPage.content.form.namePlaceholder')}
                    className="bg-transparent w-full p-2 border border-[#05213F] rounded-md"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>

                <div className="w-full space-y-2">
                  <label
                    className="text-[#21272A] font-medium text-left text-base md:text-lg"
                    htmlFor="company"
                  >
                    {t('ContactUsPage.content.form.company')}
                  </label>
                  <input
                    type="text"
                    required
                    id="company"
                    name="company"
                    placeholder={t('ContactUsPage.content.form.companyPlaceholder')}
                    className="bg-transparent w-full p-2 border border-[#05213F] rounded-md"
                    value={formData.company}
                    onChange={(e) =>
                      setFormData({ ...formData, company: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="md:flex gap-24 space-y-4 md:space-y-0">
                <div className="w-full space-y-2">
                  <label
                    className="text-[#21272A] font-medium text-left text-base md:text-lg"
                    htmlFor="email"
                  >
                    {t('ContactUsPage.content.form.email')}
                  </label>
                  <input
                    type="email"
                    required
                    id="email"
                    placeholder={t('ContactUsPage.content.form.emailPlaceholder')}
                    className="bg-transparent w-full p-2 border border-[#05213F] rounded-md"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>
                <div className="w-full space-y-2">
                  <label
                    className="text-[#21272A] font-medium text-left text-base md:text-lg"
                    htmlFor="phone"
                  >
                    {t('ContactUsPage.content.form.phone')}
                  </label>
                  <input
                    type="text"
                    required
                    id="phone"
                    name="phone"
                    placeholder={t('ContactUsPage.content.form.phonePlaceholder')}
                    className="bg-transparent w-full p-2 border border-[#05213F] rounded-md"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="md:flex gap-24 space-y-4 md:space-y-0">
                <div className="w-full space-y-2">
                  <label
                    className="text-[#21272A] font-medium text-left text-base md:text-lg"
                    htmlFor="city"
                  >
                    {t('ContactUsPage.content.form.city')}
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    placeholder={t('ContactUsPage.content.form.cityPlaceholder')}
                    className="bg-transparent w-full p-2 border border-[#05213F] rounded-md"
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                  />
                </div>
                <div className="w-full space-y-2">
                  <label
                    className="text-[#21272A] font-medium text-left text-base md:text-lg"
                    htmlFor="project"
                  >
                    {t('ContactUsPage.content.form.project')}
                  </label>
                  <input
                    required
                    type="text"
                    id="project"
                    name="project"
                    placeholder={t('ContactUsPage.content.form.projectPlaceholder')}
                    className="bg-transparent w-full p-2 border border-[#05213F] rounded-md"
                    value={formData.project}
                    onChange={(e) =>
                      setFormData({ ...formData, project: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label
                  className="text-[#21272A] font-medium text-left text-base md:text-lg"
                  htmlFor="message"
                >
                  {t('ContactUsPage.content.form.message')}
                </label>
                <textarea
                  required
                  id="message"
                  name="message"
                  placeholder={t('ContactUsPage.content.form.messagePlaceholder')}
                  className="bg-transparent w-full p-2 border border-[#05213F] rounded-md min-h-[96px]"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  required
                  type="checkbox"
                  id="checkbox"
                  className="bg-transparent h-[16px] w-[16px] border border-[#05213F] rounded-md"
                />
                <p className="text-[#21272A] font-medium text-left text-sm md:text-base">
                  {t('ContactUsPage.content.form.checkbox')}
                </p>
              </div>
              <button type="submit" className="bg-[#0B4494] hover:bg-[#0271BA] transition-all duration-300 text-white px-10 py-2 rounded-2xl">
                {isSubmitting ? t('ContactUsPage.content.form.submitting') : t('ContactUsPage.content.form.submit')}
              </button>
            </form>
            {submitSuccess && (
              <p className="my-2 text-green-500 text-left text-sm md:text-base">
                {t('ContactUsPage.content.form.successMessage')}
              </p>
            )}
            {submitError && (
              <p className="my-2 text-red-500 text-left text-sm md:text-base">
                {t('ContactUsPage.content.form.errorMessage')}
              </p>
            )}
          </div>
        </div>

        <div className="w-full h-[300px] mt-16">
          <iframe
            title="Ubicación Sisacero"
            src="https://www.google.com/maps?q=Sifuentes,%2065515%20Salinas%20Victoria,%20N.L.&output=embed"
            className="w-full h-full border-0 rounded-xl"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
}
